import React, {useContext, useMemo, useState} from 'react';
import {AgentContext} from "../../context/agent-context.jsx";
import ValidateCpf from "../../../../../../components/validation/validate-cpf.jsx";
import {useQuery} from "@tanstack/react-query";
import UserShared from "../user-shared.jsx";
import teamService from "../../../../../../services/web/team-service.js";
import {Card} from "../../../../../../components/card/card.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import optionsUtils from "../../../../../../utils/options-utils.js";
import {getCities, getNeighborhoods, getStreets} from "../../../../../../utils/location-utils.js";
import InputDateTime from "../../../../../../components/input/input-datetime.jsx";
import InputCheckbox from "../../../../../../components/input/input-checkbox.jsx";
import {FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";
import {addDays, endOfDay, format, isAfter, isWithinInterval, startOfDay} from "date-fns";
import {validateExistsCpf} from "../exists-cpf.js";
import {MaskDefault} from "../../../../../../utils/mask.js";
import {FaCalendar} from "react-icons/fa";

const General = ({nameRequiredInvalid = false, nameInputRef}) => {
    const {user, setChanges, id, setUser, cpfFocus, setExistsCpf, existsCpf} = useContext(AgentContext);
    const [oldCpf, setOldCpf] = useState(user.general.cpf)
    const [isValid, setIsValid] = useState(true);

    const {data: teams} = useQuery({
        queryFn: async () => {
            const {data} = await teamService.getAll();
            return data ?? [];
        },
        queryKey: ['teams'],
    });

    const contractualTermStyle = useMemo(() => {
        const givenDate = user.general.profile.contractualTerm;
        if (!givenDate) return {inputClass: "", labelClass: "", customIcon: undefined};

        const todayStart = startOfDay(new Date());
        const givenDateEnd = givenDate ? endOfDay(givenDate) : null
        const almostDate = addDays(todayStart, 45);

        let inputClass = "";
        let labelClass = "";
        let faIcon = <FaCalendar className={"absolute top-[9px] right-[10px]"}/>;
        if (givenDateEnd) {
            if (!isAfter(givenDateEnd, todayStart)) {
                inputClass = "!text-[#dc4c64] !border-[#dc4c64]";
                labelClass = "!text-[#dc4c64]"
                faIcon = <FaTriangleExclamation color={"dc4c64"} className={"absolute top-[9px] right-[10px]"} title={"Prazo contratual vencido"}/>
            } else if (isWithinInterval(givenDateEnd, {start: todayStart, end: almostDate})) {
                inputClass = "!text-[#FC6D00] !border-[#FC6D00]";
                labelClass = "!text-[#FC6D00]"
                faIcon = <FaCircleExclamation color={"#FC6D00"} className={"absolute top-[9px] right-[10px]"} title={"Prazo contratual próximo do vencimento"}/>
            }
        }

        return {
            inputClass: inputClass,
            labelClass: labelClass,
            customIcon: faIcon,
        };
    }, [user.general.profile.contractualTerm]);

    const onChange = ({target: {value, id}}) => handleInputs(value, id)
    const onChangeCheckBox = (value, id) => handleInputs(value, id)
    const onChangeSelection = (object, id, value) => handleInputs(value, id, object);

    const handleInputObject = (object, e) => {
        const {value, id} = e.target;
        handleInputs(value, id, object);
    }

    const handleCheckBox = (element) => {
        let {id, checked} = element.target;

        handleInputs(checked, id, "profile")
    }

    const handleInputs = (value, id, object) => {
        if (object) {
            setUser(prev => ({
                ...prev,
                general: { ...prev.general, [object]: {...(prev.general?.[object] || {}), [id]: value}}
            }));
        } else {
            setUser(prev => ({...prev, general: {...user.general, [id]: value}}))
        }

        setChanges('general');
    }

    const handleDate = (id, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd") : null
        if (user.general.profile[id] === dateFormated) return

        handleInputs(dateFormated, id, "profile");
    }

    const onChangeCpf = async ({target: {value, id}}) => {
        const regexCpf = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
        if (regexCpf.test(value)) {
            await handleCPF(value)
        }

        handleInputs(value, id)
    }

    const handleCPF = async (value) => {
        if (!value) return;

        const ok = await validateExistsCpf(value, id);

        let cpfToValidate = value
        if (ok) {
            setExistsCpf(true);
            setOldCpf(value);
        } else if (oldCpf) {
            cpfToValidate = oldCpf
            handleInputs(oldCpf, "cpf");
            setExistsCpf(false);
        }

        ValidateCpf(cpfToValidate, setIsValid);
    }

    return (
        <div className={"h-[calc(100vh-131px)] overflow-x-hidden overflow-y-auto"}>
            <Card>
                <Card.Header>
                    <Card.Title>Informações básicas</Card.Title>
                </Card.Header>
                <Card.Body className={"!py-4 !px-2"}>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-5">
                            <InputForm
                                id="name"
                                value={user.general.name}
                                label="Nome *"
                                required
                                invalid={nameRequiredInvalid}
                                inputClass={nameRequiredInvalid ? "!border-[2px] !border-[#dc2626] focus:!border-[#dc2626]" : ""}
                                inputRef={nameInputRef}
                                onChange={onChange}
                            />
                        </div>

                        <div className="col-span-2 relative">
                            <InputForm
                                id="cpf"
                                label="CPF"
                                autoFocus={cpfFocus}
                                value={user.general.cpf}
                                inputClass={isValid ? "" : "!text-[#dc4c64] !border-[#dc4c64]"}
                                labelClass={isValid ? "" : "!text-[#dc4c64]"}
                                onChange={onChangeCpf}
                                mask={'000.000.000-00'} />
                            {
                                !isValid &&
                                <>
                                    <small className={"text-[#dc4c64] absolute top-[-14px] right-0"}>CPF inválido</small>
                                    <FaTriangleExclamation color={"#dc4c64"} className={"absolute top-[9px] right-[10px]"} />
                                </>
                            }
                        </div>
                        <div className="col-span-5">
                            <InputForm value={user.general.email} label="E-Mail" id={"email"} onChange={onChange}/>
                        </div>
                    </div>

                    {
                        id &&
                        <div className="grid grid-cols-12 gap-4 mt-2">
                            <div className="col-span-7"></div>
                            <div className="col-span-5">
                                <InputSwitch
                                    id="receiveSentryNews"
                                    label="Permitir que o e-mail seja usado para receber novidades do Sentry"
                                    checked={user.general.receiveSentryNews}
                                    onChange={(checked) => onChangeCheckBox(checked, "receiveSentryNews")}
                                    disabled={!user.user.status || !user.general.email}
                                />
                            </div>
                        </div>
                    }
                </Card.Body>
            </Card>

            <Card className={"mt-2"}>
                <Card.Header>
                    <Card.Title>Informações complementares</Card.Title>
                </Card.Header>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4 mb-3">
                        <div className="col-span-2">
                            <SelectComponent
                                name="sex"
                                value={optionsUtils.sex.find(g => g.value === user.general.profile?.sex)}
                                options={optionsUtils.sex}
                                label={"Sexo"}
                                onChange={(selected) => onChangeSelection("profile", "sex", selected?.value)}
                            />
                        </div>

                        <div className={"col-span-3"}>
                            <InputDateTime
                                dateFormat="L"
                                id={"dtBirth"}
                                selected={user.general.profile.dtBirth}
                                label={"Nascimento"}
                                onChange={(date) => handleDate("dtBirth", date)}
                            />
                        </div>

                        <div className="col-span-3">
                            <SelectComponent
                                loadOptions={getCities}
                                value={optionsUtils.getValue(user.general.profile?.nationality)}
                                isAsync={true}
                                label={"Naturalidade"}
                                onChange={(selected) => onChangeSelection("profile", "nationality", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>

                        <div className="col-span-2">
                            <SelectComponent
                                name="educationLevel"
                                value={optionsUtils.educationLevel.find(g => g.value === user.general.profile?.educationLevel)}
                                options={optionsUtils.educationLevel}
                                label={"Escolaridade"}
                                onChange={(selected) => onChangeSelection("profile", "educationLevel", selected?.value)}
                            />
                        </div>

                        <div className="col-span-2">
                            <SelectComponent
                                id="maritalStatus"
                                value={optionsUtils.maritalStatus.find(m => m.value === user.general.profile?.maritalStatus)}
                                options={optionsUtils.maritalStatus}
                                label={"Estado civil"}
                                onChange={(selected) => onChangeSelection("profile", "maritalStatus", selected?.value)}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                            <InputForm id="mother" value={user.general.profile?.mother} label="Mãe"
                                      onChange={(e) => handleInputObject("profile", e)}
                            />
                        </div>

                        <div className="col-span-3">
                            <InputForm id="father" value={user.general.profile?.father} label="Pai"
                                      onChange={(e) => handleInputObject("profile", e)}
                            />
                        </div>

                        <div className="col-span-2">
                            <InputForm id="bloodFactor" value={user.general.profile?.bloodFactor}
                                      label="Fator sanguíneo"
                                      onChange={(e) => handleInputObject("profile", e)}
                            />
                        </div>

                        <div className={"col-span-2"}>
                            <InputDateTime
                                dateFormat="L"
                                id={"pictureDate"}
                                selected={user.general.profile.pictureDate}
                                label={"Data da foto"}
                                onChange={(date) => handleDate("pictureDate", date)}
                            />
                        </div>

                        <div className="col-span-2">
                            <InputCheckbox id="leftHanded" checked={user.general.profile?.leftHanded}
                                         label="Canhoto"
                                         onChange={handleCheckBox}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className={"mt-2"}>
                <Card.Header>
                    <Card.Title>Endereço</Card.Title>
                </Card.Header>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4 mb-3">
                        <div className="col-span-2">
                            <SelectComponent
                                id="type"
                                value={optionsUtils.addressType.find(a => a.value === user.general.address?.type)}
                                options={optionsUtils.addressType}
                                label={"Tipo"}
                                onChange={(selected) => onChangeSelection("address", "type", selected?.value)}
                            />
                        </div>
                        <div className="col-span-6">
                            <SelectComponent
                                loadOptions={getStreets}
                                value={optionsUtils.getValue(user.general.address?.address)}
                                isAsync={true}
                                label={"Logradouro"}
                                onChange={(selected) => onChangeSelection("address", "address", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className="col-span-2">
                            <InputForm id="number" value={user.general.address?.number} label="Número"
                                      onChange={(e) => handleInputObject("address", e)}/>
                        </div>
                        <div className="col-span-2">
                            <InputForm id="complement" value={user.general.address?.complement} label="Complemento"
                                      onChange={(e) => handleInputObject("address", e)}/>
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-5">
                            <SelectComponent
                                loadOptions={getNeighborhoods}
                                value={optionsUtils.getValue(user.general.address?.neighborhood)}
                                isAsync={true}
                                label={"Bairro"}
                                onChange={(selected) => onChangeSelection("address", "neighborhood", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className="col-span-5">
                            <SelectComponent
                                loadOptions={getCities}
                                value={optionsUtils.getValue(user.general.address?.city)}
                                isAsync={true}
                                label={"Município"}
                                onChange={(selected) => onChangeSelection("address", "city", selected?.value)}
                                cacheOptions
                                defaultOptions={[]}
                            />
                        </div>
                        <div className="col-span-2">
                            <InputForm
                                id="zipcode"
                                label="CEP"
                                mask={MaskDefault.cep}
                                value={user.general.address?.zipcode}
                                onChange={(e) => handleInputObject("address", e)}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <Card className={"mt-2"}>
                <Card.Header>
                    <Card.Title>Dados do agente</Card.Title>
                </Card.Header>
                <Card.Body>
                    <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-6">
                            <InputForm id="nameWar" value={user.general.nameWar} label="Nome de guerra"
                                       onChange={onChange}/>
                        </div>
                        <div className="col-span-3">
                            <InputForm id="employeeCode" value={user.general.employeeCode} label="Número funcional"
                                       onChange={onChange}/>
                        </div>
                        <div className="col-span-3">
                            <InputForm id="rank" value={user.general.rank} label="Patente" onChange={onChange}/>
                        </div>
                    </div>

                    <div className="grid grid-cols-12 gap-4 mt-3">
                        <div className="col-span-4">
                            <SelectComponent
                                id="team"
                                value={teams?.find(option => option.value === user.general.userTeamId) ?? ""}
                                options={teams ?? []}
                                label={"Equipe"}
                                onChange={(selected) => handleInputs(selected?.value, "userTeamId")}
                            />
                        </div>
                        <div className="col-span-2">
                            <InputForm id="sector" value={user.general.profile?.sector} label="Setor"
                                       onChange={(e) => handleInputObject("profile", e)}
                            />

                        </div>
                        <div className={"col-span-2"}>
                            <InputDateTime
                                dateFormat="L"
                                id={"admission"}
                                selected={user.general.profile.admission}
                                label={"Admissão"}
                                onChange={(date) => handleDate("admission", date)}
                            />
                        </div>
                        <div className={"col-span-2"}>
                            <InputDateTime
                                dateFormat="L"
                                id={"terminationDate"}
                                selected={user.general.profile.terminationDate}
                                label={"Data de desligamento"}
                                onChange={(date) => handleDate("terminationDate", date)}
                            />
                        </div>
                        <div className={"col-span-2"}>
                            <InputDateTime
                                dateFormat="L"
                                id={"contractualTerm"}
                                selected={user.general.profile.contractualTerm}
                                label={"Prazo contratual"}
                                onChange={(date) => handleDate("contractualTerm", date)}
                                customIcon={contractualTermStyle.customIcon}
                                inputClass={contractualTermStyle.inputClass}
                                labelClass={contractualTermStyle.labelClass}
                            />
                        </div>
                    </div>
                </Card.Body>
            </Card>

            <div className="grid grid-cols-12 gap-4 mt-2">
                <div className="col-span-12">
                    <UserShared userId={id}/>
                </div>
            </div>
        </div>
    );
};

export default General;